import React, { useEffect } from 'react';
import type { AnalysisType } from 'config/analysisConfig';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import {
  createCPR,
  registerManualCprLabelSyncTarget,
  resetCPRView,
} from '../Renderers/vtkManagers';
import type { CPRContext } from '../Renderers/vtkManagers';
import {
  getLabelVolumeFromSeriesId,
  getVolumeFromSeriesId,
} from '../Renderers/CornerstoneManager';
import {
  makeCoronaryColorPoints,
  makeOpacityPoints,
  rgbToNormalizedARGB,
} from 'config/colorUtils';
import { angleCPRISRCapture } from 'utils/captureUtils';
import type { CaptureSetterType, CprPanelMode } from './cprPanelTypes';

export function useCprDataRenderer({
  context,
  selectedSeriesId,
  isDataReady,
  hasAorticVisualDataReady,
  mode,
  analysisType,
  branchIds,
  branchId,
  aorticBranchId,
  aorticCurrentIndex,
  panelState,
  updateCPRState,
  config,
  currentVOI,
  pool,
  rendererId,
  dimensions,
  refSetter,
  gogo,
  setGogo,
  setresetref,
  applyBoundaryEditActorVisibility,
  switchViewReport,
  disabled = false,
}: {
  context: React.MutableRefObject<CPRContext | null>;
  selectedSeriesId?: string;
  isDataReady: boolean;
  hasAorticVisualDataReady: boolean;
  mode?: CprPanelMode;
  analysisType: AnalysisType;
  branchIds: Array<Record<number, string>>;
  branchId: number;
  aorticBranchId: number;
  aorticCurrentIndex: number;
  panelState: any;
  updateCPRState: (params: any) => void;
  config: any;
  currentVOI: any;
  pool: any;
  rendererId: string;
  dimensions: { width: number; height: number };
  refSetter: React.MutableRefObject<CaptureSetterType | undefined>;
  gogo: 'STOP' | 'GO' | 'GOGO';
  setGogo: React.Dispatch<React.SetStateAction<'STOP' | 'GO' | 'GOGO'>>;
  setresetref: React.Dispatch<React.SetStateAction<boolean>>;
  applyBoundaryEditActorVisibility: () => void;
  switchViewReport: boolean;
  disabled?: boolean;
}) {
  useEffect(() => {
    if (disabled) return;
    if (!context.current) return;
    if (!selectedSeriesId) return;
    if (!isDataReady) return;
    if (!hasAorticVisualDataReady) return;
    if (!mode) return;
    if (dimensions.width <= 0 || dimensions.height <= 0) return;

    if (analysisType === 'aorticValveCalcium' || analysisType === 'tavi') {
      if (panelState.currentCPRId !== aorticBranchId) {
        updateCPRState({
          indexedState: {
            mainIndex: {
              branchId: aorticBranchId,
              index: 0,
            },
            pickPoint: { index: aorticCurrentIndex },
          },
        });
      }
    }

    const { renderingQuality } = config.generalSetting;
    const HUVolume = getVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    if (!HUVolume) return;

    const labelVolume = getLabelVolumeFromSeriesId(
      selectedSeriesId,
      renderingQuality !== 'high',
    );
    if (!labelVolume) return;

    const huData = HUVolume.imageData as vtkImageData;
    let labelData = (pool.labelData.data as Record<number, vtkImageData>)[
      branchId
    ];
    const centerData = (pool.centerData.data as Record<number, vtkPolyData>)[
      branchId
    ];
    const branchName =
      analysisType === 'aorticValveCalcium' || analysisType === 'tavi'
        ? 'Aorta'
        : (Object.entries(
            branchIds.find((obj) =>
              Object.prototype.hasOwnProperty.call(obj, branchId),
            ) ?? {},
          )[0]?.[1] as string | undefined);
    if (
      !(analysisType === 'aorticValveCalcium' || analysisType === 'tavi') &&
      !branchName
    ) {
      return;
    }

    let colorPoints: ReturnType<typeof makeCoronaryColorPoints>;
    let locOpacityPoints: ReturnType<typeof makeOpacityPoints>;

    if (
      branchName === 'Aorta' ||
      analysisType === 'aorticValveCalcium' ||
      analysisType === 'tavi'
    ) {
      labelData = labelVolume.imageData as vtkImageData;
      const colorarr = [];
      for (const clr of config.aorta_colors) {
        colorarr.push(rgbToNormalizedARGB(clr));
      }
      colorPoints = colorarr;
      locOpacityPoints = config.aorta_opacitys;
    } else {
      colorPoints = makeCoronaryColorPoints(config.colors);
      locOpacityPoints = makeOpacityPoints(analysisType);
    }

    if (
      !huData ||
      !labelData ||
      !centerData ||
      !centerData.getPoints() ||
      centerData.getPoints().getNumberOfPoints() < 2
    ) {
      return;
    }

    let cancelled = false;
    (async () => {
      await createCPR(
        context,
        huData,
        labelData,
        centerData,
        mode,
        analysisType,
        currentVOI,
        colorPoints,
        locOpacityPoints,
      );
      if (cancelled) return;
      if (!context.current) return;
      if (context.current.rendererId !== rendererId) return;
      context.current['selectedBranchNum'] = branchId;
      registerManualCprLabelSyncTarget({
        rendererId,
        branchId,
        labelData,
      });
      applyBoundaryEditActorVisibility();

      requestAnimationFrame(() => {
        if (!context.current) return;
        if (context.current.rendererId !== rendererId) return;
        if (context.current.selectedBranchNum !== branchId) return;
        resetCPRView(context);
        setresetref((prev) => !prev);
      });

      if (gogo === 'GO') {
        if (analysisType === 'isr') {
          if (refSetter.current) {
            const index = refSetter.current.branchIndex;
            angleCPRISRCapture(context, index, -90).then(({ stent, full }) => {
              refSetter.current?.setter(refSetter.current?.key, {
                [refSetter.current.keys[0]]: full,
                [refSetter.current.keys[1]]: stent,
              });

              setGogo('STOP');
            });
          }
          setGogo('STOP');
          return;
        }
        setGogo('GOGO');
      }
    })().catch((err) => {
      console.warn('[useCprDataRenderer] createCPR failed', err);
    });
    return () => {
      cancelled = true;
    };
  }, [
    context,
    selectedSeriesId,
    isDataReady,
    hasAorticVisualDataReady,
    mode,
    analysisType,
    branchIds,
    branchId,
    dimensions.height,
    dimensions.width,
    aorticBranchId,
    aorticCurrentIndex,
    refSetter.current,
    gogo,
    switchViewReport,
    disabled,
  ]);
}
